import { useEffect, useRef, type RefObject } from 'react';

type NotificationInfiniteScrollTriggerProps = {
  rootRef: RefObject<HTMLDivElement | null>;
  hasMore: boolean;
  isLoading: boolean;
  isBlocked: boolean;
  error: string | null;
  onLoadMore: () => void;
  onRetry: () => void;
};

export function NotificationInfiniteScrollTrigger({
  rootRef,
  hasMore,
  isLoading,
  isBlocked,
  error,
  onLoadMore,
  onRetry,
}: NotificationInfiniteScrollTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trigger = triggerRef.current;

    if (!trigger || !hasMore || isLoading || isBlocked || error) return;

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting) return;
        observer.unobserve(trigger);
        onLoadMore();
      },
      { root: rootRef.current, rootMargin: '0px 0px 160px 0px' }
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [error, hasMore, isLoading, isBlocked, onLoadMore, rootRef]);

  if (!hasMore && !isLoading && !error) return null;

  return (
    <div
      ref={triggerRef}
      aria-live="polite"
      className="text-on-surface-variant flex min-h-12 items-center justify-center py-3 text-xs"
    >
      {isLoading ? 'Đang tải thêm thông báo…' : null}
      {error ? (
        <button
          type="button"
          onClick={onRetry}
          className="text-primary focus-visible:ring-primary/50 rounded-lg px-3 py-2 font-semibold focus-visible:ring-2 focus-visible:outline-none"
        >
          Tải lại thông báo
        </button>
      ) : null}
    </div>
  );
}
